import { X, Stethoscope, Soup, CupSoda } from '@/components/icons'
import { Button } from './ui/button'
import { Card } from '@/components/ui/card'
import { DetailSkeleton } from './ui/skeleton-presets'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { useCrewGetById } from '@/lib/hooks'
import { formatCrewRole, getInitials } from '@/lib/utils'
import { ProfileAvatar } from './ProfileAvatar'
import { PreferenceCard } from './PAXDetailView'
import { PhoneLink, EmailLink } from './ContactLink'
import { CrewPreferencePhotos } from './CrewPreferencePhotos'

interface CrewBasicDetailDrawerProps {
  isOpen: boolean
  onClose: () => void
  crewId: string | null
}

export function CrewBasicDetailDrawer({
  isOpen,
  onClose,
  crewId,
}: CrewBasicDetailDrawerProps) {
  const { data: crew, isLoading } = useCrewGetById(crewId)

  // Group persisted preference photos by preference key for the read-only grids.
  const photosByPreference = (
    (crew as { preferencePhotos?: { preference: string; s3Key: string }[] } | undefined)
      ?.preferencePhotos ?? []
  ).reduce<Record<string, string[]>>((acc, p) => {
    acc[p.preference] = [...(acc[p.preference] ?? []), p.s3Key]
    return acc
  }, {})

  const preferencePhotoGrid = (preference: string) => {
    const s3Keys = photosByPreference[preference] ?? []
    if (s3Keys.length === 0) return undefined
    return <CrewPreferencePhotos readOnly preference={preference} s3Keys={s3Keys} />
  }

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-neutral-950">
            Crew Details
          </DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {isLoading ? (
            <DetailSkeleton rows={6} />
          ) : crew ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-3">
                <ProfileAvatar
                  className="size-18"
                  photoS3Key={crew.photoS3Key}
                  avatarColor={crew.avatarColor}
                  initials={getInitials(crew.firstName, crew.lastName)}
                  fallbackClassName="text-[35px] font-semibold uppercase text-background"
                />
              </div>
              <Card className="p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Name</span>
                    <span>{crew.name}</span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Nationality</span>
                    <span>{crew.nationality || '-'}</span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Base</span>
                    <span>{crew.baseAirportIcao || '-'}</span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Telephone</span>
                    <PhoneLink phone={crew.phone} phoneCountry={crew.phoneCountry} />
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Email</span>
                    <EmailLink email={crew.email} />
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Date of Birth</span>
                    <span>{crew.dateOfBirth || '-'}</span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Crew Role</span>
                    <span>{formatCrewRole(crew.crewRole) || '-'}</span>
                  </div>
                </div>
              </Card>

              <PreferenceCard
                icon={<Stethoscope className="size-4 text-content-brand-bold" />}
                title="Medical Information"
                value={crew.medicalInfo || '-'}
                photos={preferencePhotoGrid('medicalInfo')}
              />

              <PreferenceCard
                icon={<Soup className="size-4 text-content-brand-bold" />}
                title="Food Preferences"
                value={crew.foodPreferences || '-'}
                photos={preferencePhotoGrid('foodPreferences')}
              />

              <PreferenceCard
                icon={<CupSoda className="size-4 text-content-brand-bold" />}
                title="Beverage Preferences"
                value={crew.beveragePreferences || '-'}
                photos={preferencePhotoGrid('beveragePreferences')}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">Crew member not found</p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
